import { setSendCustomerEvent, trackGAEvent } from '@moonpig/web-core-analytics'
import { FieldErrors } from 'react-hook-form'
import { isLocalStorageAvailable } from '@moonpig/web-core-utils'
import { AuthenticationFormInput, FormStep } from './types'

function getTrackableStep(formStep?: FormStep) {
  switch (formStep) {
    case FormStep.CODE_LOGIN:
      return {
        action: 'login with email and passcode',
        content_type:
          'account | sign in with code | authentication wizard | passcode form',
        label: 'authentication wizard | enter the code we sent you',
      }
    case FormStep.LOGIN:
      return {
        action: 'login enter password',
        content_type: 'account | login enter password | authentication wizard',
        label: 'authentication wizard',
      }
    case FormStep.REGISTER:
      return {
        action: 'sign up',
        content_type:
          'account | sign up | authentication wizard | enter personal details',
        label: 'authentication wizard | enter personal details',
      }
    case FormStep.WELCOME:
      return {
        action: 'welcome sign up or login',
        content_type: 'account | welcome | authentication wizard',
        label: 'authentication wizard | enter email address',
      }
    case FormStep.CODE_RECOVERY:
      return {
        action: 'account recovery with email and passcode',
        content_type:
          'account | sign in with code | authentication wizard | passcode recovery | passcode form',
        label:
          'authentication wizard | passcode recovery | enter the code we sent you',
      }
    default:
      return {
        action: 'reset password',
        content_type: 'account | reset password | authentication wizard',
        label: 'authentication wizard',
      }
  }
}

export const trackAuthenticationImpression = (formStep: FormStep) => {
  const { action, content_type, label } = getTrackableStep(formStep)
  trackGAEvent({
    event: 'impression',
    content_data: {
      content_type,
      item_id: undefined,
    },
    event_data: {
      category: 'account',
      action,
      label,
      non_interaction: true,
      value: undefined,
    },
  })
}

export const trackAuthenticationEmailChange = (
  formStep: FormStep,
  content: string,
) => {
  const step = formStep.toLowerCase()
  trackGAEvent({
    event: 'select_content',
    content_data: {
      content_type: `authentication | ${step} | ${content}`,
      item_id: undefined,
    },
    event_data: {
      category: 'authentication',
      action: step,
      label: content,
      non_interaction: true,
      value: undefined,
    },
    discount_data: undefined,
    ecommerce: undefined,
    error_data: undefined,
  })
}

export const trackForgotPasswordImpression = () => {
  const { content_type, action, label } = getTrackableStep()
  trackGAEvent({
    event: 'impression',
    content_data: {
      content_type,
      item_id: undefined,
    },
    event_data: {
      category: 'account',
      action,
      label,
      non_interaction: true,
      value: undefined,
    },
  })
}

const trackAuthenticationSelectContent = ({
  content_type,
  action,
  label,
}: {
  content_type: string
  action: string
  label: string
}): void => {
  trackGAEvent({
    event: 'select_content',
    content_data: {
      content_type,
      item_id: undefined,
    },
    event_data: {
      category: 'account',
      action,
      label,
      non_interaction: true,
      value: undefined,
    },
    discount_data: undefined,
    ecommerce: undefined,
    error_data: undefined,
  })
}

export const trackAuthenticationBackCTA = (formStep: FormStep): void => {
  switch (formStep) {
    case FormStep.LOGIN:
      trackAuthenticationSelectContent({
        content_type:
          'account | login with email and password | authentication wizard | back to login',
        action: 'login with email and password',
        label: 'authentication wizard | back to login',
      })
      break
    case FormStep.CODE_LOGIN:
      trackAuthenticationSelectContent({
        content_type:
          'account | sign in with code | authentication wizard | back to login',
        action: 'login with email and passcode',
        label: 'authentication wizard | back to login',
      })
      break
    case FormStep.CODE_RECOVERY:
      trackAuthenticationSelectContent({
        content_type:
          'account | sign in with code | authentication wizard | passcode recovery | back to login',
        action: 'account recovery with email and passcode',
        label: 'authentication wizard | passcode recovery | back to login',
      })
      break
    case FormStep.REGISTER:
      trackAuthenticationSelectContent({
        content_type:
          'account | sign up | authentication wizard | back to login',
        action: 'sign up',
        label: 'authentication wizard | back to login',
      })
      break
    /* istanbul ignore next */
    default:
      break
  }
}

export const trackAuthenticationError = ({
  event,
  action,
  label,
  loginMethod,
  error,
  isLoggedIn,
}: {
  event: 'login_failure' | 'sign_up_failure' | 'login_error' | 'sign_up_error'
  action: string
  label: string
  loginMethod: 'password' | 'passcode' | 'magic link' | 'passcode recovery'
  error: string
  isLoggedIn?: boolean
}) => {
  trackGAEvent({
    event,
    user_data: {
      is_logged_in: isLoggedIn ?? false,
      login_method: loginMethod,
    },
    event_data: {
      category: 'account',
      action,
      label,
      non_interaction: true,
      value: undefined,
    },
    error_data: {
      id: 0,
      message: error.toLowerCase(),
    },
  })
}

export const trackAuthenticationValidationErrors = (
  formStep: FormStep,
  errors: FieldErrors<AuthenticationFormInput>,
) => {
  const fields = Object.keys(errors)
    .map(field => field.toLowerCase())
    .join(',')
  switch (formStep) {
    case FormStep.LOGIN:
      trackAuthenticationError({
        event: 'login_error',
        action: 'login error',
        label: `password`,
        loginMethod: 'password',
        error: fields,
      })

      break
    case FormStep.REGISTER:
      trackAuthenticationError({
        event: 'sign_up_error',
        action: 'sign up error',
        label: `password`,
        loginMethod: 'password',
        error: fields,
      })
      break
    /* istanbul ignore next */
    default:
      break
  }
}

export const trackForgotPasswordSuccessfulDismiss = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | request password reset email success | authentication wizard | dismiss',
    action: 'request password reset email success',
    label: 'authentication wizard | dismiss',
  })

export const trackForgotPasswordCancelModal = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | cancel reset password | authentication wizard | dismiss',
    action: 'cancel reset password',
    label: 'authentication wizard | dismiss',
  })

export const trackForgotPasswordRequestPasswordReset = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | request password reset email | authentication wizard | reset password',
    action: 'request password reset email',
    label: 'authentication wizard | reset password',
  })

export const trackForgotPasswordEmailSent = () =>
  trackGAEvent({
    event: 'impression',
    content_data: {
      content_type:
        'account | password reset email sent | authentication wizard',
      item_id: undefined,
    },
    event_data: {
      category: 'account',
      action: 'password reset email sent',
      label: 'authentication wizard',
      non_interaction: true,
      value: undefined,
    },
  })

export const trackAuthenticationContinueCTA = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | login submit email address | authentication wizard | continue',
    action: 'login submit email address',
    label: 'authentication wizard | continue',
  })

export const trackAuthenticationLoginCTA = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | login with email and password | authentication wizard | login',
    action: 'login with email and password',
    label: 'authentication wizard | login',
  })

export const trackAuthenticationRegisterCTA = () =>
  trackAuthenticationSelectContent({
    content_type: 'account | sign up | authentication wizard | create account',
    action: 'sign up',
    label: 'authentication wizard | create account',
  })

export const trackAuthenticationMagicLinkSuccess = (skipped: boolean) => {
  const label = skipped ? 'magic link | skipped' : 'magic link'

  if (!skipped) {
    setSendCustomerEvent({ loginMethod: label })
  }

  if (isLocalStorageAvailable()) {
    localStorage.setItem('mnpg_send_ga_login_event', label)
  }
}

export const trackAuthenticationSignInWithCodeCTA = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | sign in with code | authentication wizard | button',
    action: 'login with email and passcode',
    label: 'authentication wizard | sign in with code',
  })

export const trackAuthenticationSignInWithPasswordCTA = () => {
  trackGAEvent({
    event: 'select_content',
    event_data: {
      category: 'account',
      action: 'login with passcode',
      label: 'authentication wizard | login with email and password',
    },
  })
}

export const trackAuthenticationOnPasscodeFormSubmit = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | sign in with code | authentication wizard | passcode submit',
    action: 'login with email and passcode',
    label: 'authentication wizard | submit passcode',
  })

export const trackAuthenticationAccountRecoveryOnPasscodeFormSubmit = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | sign in with code | authentication wizard | passcode recovery | passcode submit',
    action: 'account recovery with email and passcode',
    label: 'authentication wizard | passcode recovery | submit passcode',
  })

export const trackAuthenticationResendTheCodeCTA = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | sign in with code | authentication wizard | resend code button',
    action: 'login with email and passcode',
    label: 'authentication wizard | resend code',
  })

export const trackAuthenticationAccountRecoveryResendTheCodeCTA = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | sign in with code | authentication wizard | passcode recovery | resend code button',
    action: 'account recovery with email and passcode',
    label: 'authentication wizard | passcode recovery | resend code',
  })

export const trackAuthenticationPasscodeError = (
  loginMethod: 'passcode' | 'passcode recovery',
  errorMessage: string,
) =>
  trackAuthenticationError({
    event: 'login_failure',
    action: 'login failure',
    label: loginMethod,
    loginMethod,
    error: errorMessage,
    isLoggedIn: false,
  })

export const trackAuthenticationMagicLinkFailure = (
  errorMessage: string | undefined,
  skipped: boolean,
) => {
  const label = skipped ? 'magic link | skipped' : 'magic link'

  trackAuthenticationError({
    event: 'login_failure',
    action: 'login failure',
    label,
    loginMethod: 'magic link',
    error: errorMessage ?? 'unspecified error',
    isLoggedIn: skipped,
  })
}

export const trackAuthenticationRegisterSuccess = (
  isEmailUnreachable: boolean,
) => {
  trackGAEvent({
    event: 'sign_up',
    event_data: {
      category: 'account',
      action: 'sign up success',
      label: isEmailUnreachable ? 'unreachable email' : 'password',
      non_interaction: true,
      value: undefined,
    },
  })

  setSendCustomerEvent({
    loginMethod: 'password',
  })
}

export const trackAuthenticationRegisterFailure = (errorMessage?: string) => {
  trackAuthenticationError({
    event: 'sign_up_failure',
    action: 'sign up failure',
    label: 'password',
    loginMethod: 'password',
    error: errorMessage ?? /* istanbul ignore next */ 'unspecified error',
  })
}

export const trackAuthenticationLoginSuccess = (loginMethod: string) => {
  trackGAEvent({
    event: 'login',
    event_data: {
      category: 'account',
      action: 'login success',
      label: loginMethod,
      non_interaction: true,
      value: undefined,
    },
  })

  setSendCustomerEvent({
    loginMethod,
  })
}

export const trackAuthenticationLoginFailure = (errorMessage?: string) => {
  trackAuthenticationError({
    event: 'login_failure',
    action: 'login failure',
    label: 'password',
    loginMethod: 'password',
    error: errorMessage ?? /* istanbul ignore next */ 'unspecified error',
    isLoggedIn: false,
  })
}

export const trackForgotPasswordCTA = () =>
  trackAuthenticationSelectContent({
    content_type:
      'account | login with email and password | authentication wizard | forgot password',
    action: 'login with email and password',
    label: 'authentication wizard | forgot password',
  })

export const trackValidateEmailInvalidImpression = () => {
  trackGAEvent({
    event: 'impression',
    content_data: {
      content_type:
        'account | invalid email address alert | authentication wizard | zerobounce modal',
      item_id: undefined,
    },
    event_data: {
      category: 'account',
      action: 'invalid email address',
      label: 'authentication wizard | zerobounce modal',
      non_interaction: true,
      value: undefined,
    },
  })
}

export const trackValidateEmailModalDismiss = () => {
  trackAuthenticationSelectContent({
    content_type:
      'account | dismiss modal | authentication wizard | zerobounce modal',
    action: 'dismiss modal',
    label: 'authentication wizard | zerobounce modal',
  })
}

export const trackPrivateEmailModalImpression = () => {
  trackGAEvent({
    event: 'impression',
    content_data: {
      content_type:
        'account | social login | authentication wizard | private email modal',
      item_id: undefined,
    },
    event_data: {
      category: 'account',
      action: 'social login',
      label: 'authentication wizard | private email modal',
      non_interaction: true,
      value: undefined,
    },
  })
}

export const trackPrivateEmailModalCTA = () => {
  trackAuthenticationSelectContent({
    content_type:
      'account | social login | authentication wizard | private email modal',
    action: 'continue',
    label: `authentication wizard | private email modal`,
  })
}

export const trackSocialLoginOptionClick = (provider: string) =>
  trackAuthenticationSelectContent({
    content_type: `account | social login | authentication wizard | continue with ${provider}`,
    action: 'social login',
    label: `continue with ${provider}`,
  })

export const trackSocialLoginOptionsLoadErrorImpression = () => {
  trackGAEvent({
    event: 'impression',
    content_data: {
      content_type:
        'account | social login | authentication wizard | social login unavailable message',
      item_id: undefined,
    },
    event_data: {
      category: 'account',
      action: 'social login',
      label: 'social login unavailable message',
      non_interaction: true,
      value: undefined,
    },
  })
}

export const trackAccountRecoveryClick = (context: string) =>
  trackAuthenticationSelectContent({
    content_type: `account | social login - account recovery | authentication wizard | ${context} - account recovery`,
    action: 'social login - account recovery',
    label: `${context} - account recovery`,
  })

export const trackSocialException = ({
  label,
  errorCode = '0',
  errorMessage,
}: {
  label: string
  errorCode?: number | '0'
  errorMessage: string
}) => {
  trackGAEvent({
    event: 'social_authentication_exception',

    user_data: {
      is_logged_in: false,
      login_method: label as 'google' | 'apple',
    },

    event_data: {
      category: 'account',
      action: 'social authentication exception',
      label,
      non_interaction: true,
      value: undefined,
    },

    error_data: {
      id: errorCode,
      message: errorMessage,
    },
  })
}
